/**
 * App.tsx
 * [Readme] --------------------------------------------------------------------------------------
 * 
 * index.tsx에서 접근하는 실질적인 리액트 앱 컴포넌트의 시작점입니다.
 * ※웬만한 경우 이 영역을 조작할 필요는 없습니다.
 * 
 * -----------------------------------------------------------------------------------------------
 */

import { useEffect, lazy, Suspense } from 'react';
import { isMobile } from 'react-device-detect';
import styled, { css } from 'styled-components';
import { observer, useLocalObservable } from 'mobx-react';
import { cssTransition, ToastContainer } from 'react-toastify';

// import DeskTop from 'pages/desktop';
// import Mobile from 'pages/mobile';
import MobxStore from 'common-modules/general/Store/MobxStore';
import MainModel from 'common-modules/general/Common/Logic/Main/MainSalesCommon';
import useGaTracker from 'common-modules/general/Modules/custom-hooks/useGaTracker';

const DeskTop = lazy(() => import('pages/desktop'));
const Mobile = lazy(() => import('pages/mobile'));

const Wrapper = styled.div<{ isMobile: boolean }>`
  ${props =>
    props.isMobile
      ? css``
      : css`
          min-width: 1300px;
        `}
`;

const customToasterTransition = cssTransition({
  enter: 'toaster-fade-in',
  exit: 'toaster-fade-out'
});

const App = observer(() => {
  const { authManager } = MobxStore();

  /**
   * 분양페이지의 기본 데이터를 서버에서 가져옵니다.
   */
  const { model: mainModel } = useLocalObservable(() => ({ model: new MainModel() }));
  useEffect(() => {
    mainModel.getData();
  }, []);

  /**
   * IE11 대응 코드입니다. 사실상 deprecated입니다.
   */
  useEffect(() => {
    if (navigator.userAgent.match(/Trident\/7\./)) {
      document.body.addEventListener('mousewheel', function (event: any) {
        event.preventDefault();
        var wd = event.wheelDelta;
        var csp = window.pageYOffset;
        window.scrollTo(0, csp - wd);
      });
    }

    /**
     * @deprecated
     * 카카오톡 관련 오픈 API 사용 초기화 구문
     */
    // const jsKey = process.env.REACT_APP_KAKAO_KEY;
    // if (jsKey) {
    //   Kakao.init(jsKey);
    // }
  }, []);

  /**
   * authManager를 통해 현재 로그인이 되었는지 판별합니다.
   */
  useEffect(() => {
    authManager.upToDateUserDataToToken();
  }, []);

  /**
   * GA 트래킹을 시작합니다.
   * /src/useGaTracker.tsx 참고.
   */
  useGaTracker();

  /**
   * @render
   * 렌더부입니다. App.tsx에서는 데스크탑과 모바일을 감지하고 이를 통해 적응형 레이아웃을 결정합니다.
   */
  return (
    <>
      {/* 현재 기기가 데스크탑 or 모바일인 것을 감지하고 해당하는 레이아웃을 표기합니다. */}
      <Suspense fallback={<></>}>
      {isMobile ? (
        <Wrapper isMobile={isMobile}>
          <Mobile mainModel={mainModel} />
        </Wrapper>
      ) : (
        <Wrapper isMobile={isMobile}>
          <DeskTop mainModel={mainModel} />
        </Wrapper>
      )}
      </Suspense>
      {/* 
        토스터 컨테이너
        하단의 팝오버를 띄우는 컴포넌트로 최상단에 위치해야 하기 떄문에 해당 위치에 설정 했습니다.
       */}
      <ToastContainer
        transition={customToasterTransition}
        position={isMobile ? 'top-center' : 'bottom-center'}
        autoClose={1000}
        pauseOnHover={false}
        closeOnClick={false}
        pauseOnFocusLoss={false}
        hideProgressBar
        limit={1}
        rtl={false}
        draggable={false}
        className={`custom-toaster ${isMobile && 'mobile'}`}
        toastClassName="toaster-root"
      />
    </>
  );
});

export default App;
